import { useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormSelect from "../form-input/formSelect";
import { FilterField } from "../../models/types/common";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { useResponsive } from "../../hooks/mediaQuery";
import { useTranslation } from "react-i18next";

type PageFilterProps = {
    fields: FilterField[];
    validationSchema?: yup.AnyObjectSchema;
    onFilterChange?: (filters: Record<string, any>) => void;
    onSuccess?: () => void;
};

type FieldRenderProps = {
    field: FilterField;
    register: ReturnType<typeof useForm>["register"];
}

const PageFilter: React.FC<PageFilterProps> = ({ fields = [], validationSchema, onFilterChange, onSuccess }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { isXs, isSm, isMd } = useResponsive();
    const tablet = isSm || isMd;
    const { t } = useTranslation();

    // Tạo giá trị mặc định từ URL
    const defaultValues = useMemo(() => {
        const obj: Record<string, any> = {};
        fields.forEach(f => {
            const urlValue = searchParams.get(f.name);
            if (f.type === 'dateFromto') {
                const [start, end] = urlValue?.split(',') ?? [null, null];
                obj[f.name] = [start, end];
            } else if (f.type === 'select') {
                obj[f.name] = urlValue ?? "all";
            } else if (f.type === 'text') {
                obj[f.name] = urlValue ?? "";
            }
        });
        return obj;
    }, [fields, searchParams]);

    // Khởi tạo react-hook-form
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        control,
    } = useForm({
        defaultValues,
        resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    });

    // Đồng bộ form khi URL thay đổi
    useEffect(() => {
        if (defaultValues) {
            reset(defaultValues);
        }
    }, [defaultValues, reset]);

    // Xử lý submit filter
    const onSubmit = (data: Record<string, any>) => {
        const newParams = new URLSearchParams(location.search);
        fields.forEach(f => {
            const val = data[f.name];
            if (val === "all") newParams.delete(f.name);
            if (val && val !== "all" && f.type !== 'dateFromto') newParams.set(f.name, val);
            if (f.type === 'dateFromto') {
                const [start, end] = data[f.name] ?? [null, null];
                if (start && end) newParams.set(f.name, `${start},${end}`);
            }
        });
        newParams.delete("pageIndex");
        setSearchParams(newParams);
        onSuccess?.();
        if (onFilterChange) {
            const cleaned: Record<string, any> = {};
            Object.entries(data).forEach(([k, v]) => {
                if (v && v !== "all" && k !== 'dateFromto') cleaned[k] = v;
            });
            onFilterChange(cleaned);
        }
    };

    // Xử lý reset filter
    const onReset = () => {
        reset();
        setSearchParams(new URLSearchParams());
        onFilterChange?.({});
        onSuccess?.();
    };

    const getErrorMessage = (error: unknown): string | undefined => {
        if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
            return (error as any).message;
        }
        return undefined;
    };
    // UI

    const RenderField: React.FC<FieldRenderProps> = ({ field, register }) => {
        const commonProps = {
            id: field.name,
            className: "min-w-[220px] w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400",
            ...register(field.name),
        };

        switch (field.type) {
            case "text":
                return <input type="text" placeholder={field.placeholder} {...commonProps} />;
            case "select":
                return (
                    <FormSelect
                        options={field.options}
                        name={field.name}
                        placeholder={field.placeholder}
                        control={control}
                        className='min-w-[220px]'
                    />
                );
            case "date":
                return <input type="date" {...commonProps} />;
            case 'dateFromto':
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                            name={field.name}
                            control={control}
                            render={({ field: { onChange, value } }: { field: { onChange: (value: [string | null, string | null]) => void, value: [string | null, string | null] } }) => (
                                <DateRangePicker
                                    className="date-range-picker filter_date w-full"
                                    format="DD/MM/YYYY"
                                    value={
                                        value && (Array.isArray(value) && value.length === 2
                                            ? [value[0] ? dayjs(value[0]) : null, value[1] ? dayjs(value[1]) : null]
                                            : [null, null])
                                    }
                                    onChange={(newValue) => {
                                        if (Array.isArray(newValue)) {
                                            onChange([
                                                newValue[0] ? dayjs(newValue[0]).format('YYYY-MM-DD HH:mm') : null,
                                                newValue[1] ? dayjs(newValue[1]).add(1439, 'minute').format('YYYY-MM-DD HH:mm') : null,
                                            ]);
                                        } else {
                                            onChange([null, null]);
                                        }
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                );
            default:
                return null;
        }
    };

    if (!fields.length) return null;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full rounded-lg border border-gray-200 shadow-sm bg-white p-[8px_12px_12px]">
            <div className="flex flex-wrap items-end gap-3">
                {fields.map(field => (
                    <div key={field.name} className={isXs ? "w-full" : ""}>
                        {field.label && (
                            <label htmlFor={field.name} className="block text-sm font-medium mb-1 text-gray-700">
                                {field.label}
                            </label>
                        )}
                        <RenderField field={field} register={register} />
                        {getErrorMessage(errors?.[field.name]) && (
                            <p className="text-red-500 text-xs mt-1">{getErrorMessage(errors?.[field.name])}</p>
                        )}
                    </div>
                ))}
                <div className="mt-4 flex gap-2">
                    <button type="submit" className="bg-blue-600 text-white px-4 p-[10px_16px] rounded hover:bg-blue-700">
                        {t("common.search")}
                    </button>
                    <button type="button" onClick={onReset} className="bg-gray-300 text-black px-4 p-[10px_16px] rounded hover:bg-gray-400">
                        {t("common.reset")}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default PageFilter;
